import { Box, Button, Chip, FormControl, Input, Option, Select, Typography } from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import React from "react";
import { Util } from "../Helpers/Util";
import ProposalDetailDialog from "../Dialogs/ProposalDetailDialog";
import ProposalTrackingNotesDialog from "../Dialogs/ProposalTrackingNotesDialog";
import { Building } from "./BuildingList";
import PortalTableLayout from "../components/PortalTableLayout";

// Interface for proposal tracking records from the API
export interface ProposalTrackingRecord {
	BuildingName: string;
	InvoiceNumber: string;
	TotalAmount: number;
	InvoiceDate: Date;
	Description: string;
	Status: string;
	InvoiceHeaderId: string;
	IsApproved: boolean;
	DateMechanicSigned: Date;
	IsMechanicSigned: boolean;
	MechanicName: string;
	ActualInvoiceNumber: string;
	DateInvoiced: Date;
	NoteCount: number;
}

export default function ProposalTracking() {
	const [data, setData] = React.useState<Array<ProposalTrackingRecord>>();
	const [pageNumber, setPageNumber] = React.useState(0);
	const [pageSize, setPageSize] = React.useState(25);
	const [globalFilter, setGlobalFilter] = React.useState("");
	const [statusFilter, setStatusFilter] = React.useState("");
	const [statusOptions, setStatusOptions] = React.useState<Array<string>>([]);
	const [order, setOrder] = React.useState(true);
	const [orderBy, setOrderBy] = React.useState(0);
	const [isMyBuildings, setIsMyBuildings] = React.useState(false);

	const [modal, setModal] = React.useState(false);
	const [selectedProposal, setSelectedProposal] = React.useState<ProposalTrackingRecord | null>(null);
	const [notesModal, setNotesModal] = React.useState(false);
	const [notesProposal, setNotesProposal] = React.useState<ProposalTrackingRecord | null>(null);

	const [buildings, setBuildings] = React.useState<Array<Building>>();

	const getBuildings = () => {
		fetch("Building/GetProposalTrackingBuildings", {
			method: "Post",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ filter: true }),
		})
			.then((x) => x.json())
			.then((x) => {
				const bs = x
					.filter((y: any) => y.building_name)
					.sort((a: any, b: any) => a.building_name.localeCompare(b.building_name))
					.map((y: any) => {
						return { Name: y.building_name, ID: y.buildings_id } as Building;
					});
				setBuildings(bs);
			});
	};

	React.useEffect(() => {
		getBuildings();
		fetch("Proposal/GetProposalTrackingStatuses", {
			method: "Post",
			headers: { "Content-Type": "application/json" },
			cache: "no-cache",
			body: JSON.stringify({}),
		})
			.then((x) => x.json())
			.then((x) => {
				setStatusOptions(x.map((y: { status: string }) => y.status).filter(Boolean));
			});
	}, []);

	React.useEffect(() => {
		getData();
	}, [statusFilter, isMyBuildings]);

	React.useEffect(() => {
		setPageNumber(0);
	}, [statusFilter, isMyBuildings, globalFilter]);

	const getData = () => {
		fetch("Proposal/GetProposalTracking", {
			method: "Post",
			headers: {
				"Content-Type": "application/json",
			},
			cache: "no-cache",
			body: JSON.stringify({
				statusFilter: statusFilter || null,
				isMyBuildings: isMyBuildings,
			}),
		})
			.then((x) => x.json())
			.then((x) => {
				const res = x.map((y: any) => {
					return {
						BuildingName: y.building_name,
						InvoiceNumber: y.invoice_number,
						TotalAmount: y.total_amount,
						InvoiceDate: Util.ParseDate(y.invoice_date),
						Description: y.description,
						Status: y.status,
						InvoiceHeaderId: y.invoice_header_id,
						IsApproved: y.is_approved,
						DateMechanicSigned: Util.ParseDate(y.date_mechanic_signed),
						IsMechanicSigned: y.is_signed,
						MechanicName: y.mechanic_name,
						ActualInvoiceNumber: y.actual_invoice_number,
						DateInvoiced: Util.ParseDate(y.date_invoiced),
						NoteCount: y.note_count ?? 0,
					} as ProposalTrackingRecord;
				});
				setData(res);
			});
	};

	const formatCurrency = (amount: number) => {
		if (amount == null) return "";
		return "$" + amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
	};

	const getStatusColor = (status: string): "success" | "warning" | "neutral" => {
		if (!status) return "neutral";
		const lowerStatus = status.toLowerCase();
		if (lowerStatus === "paid" || lowerStatus === "accepted" || lowerStatus === "converted") return "success";
		if (lowerStatus === "pending") return "warning";
		return "neutral";
	};

	const dt = (data ?? []).filter(
		(x) =>
			Util.isNullOrEmpty(globalFilter) ||
			x.BuildingName?.toLowerCase().includes(globalFilter.toLowerCase()) ||
			x.InvoiceNumber?.toLowerCase().includes(globalFilter.toLowerCase()) ||
			x.Description?.toLowerCase().includes(globalFilter.toLowerCase())
	);

	const cols = ["Proposal #", "Building", "Amount", "Date", "Description", "Status", "Mechanic", "Date Signed", "Invoice #", "Date Invoiced", "Notes"];

	const old = new Date("1/1/1900");
	const parseNum = (val: string | number | null | undefined) => {
		if (val == null || val === "") return Number.NEGATIVE_INFINITY;
		const n = typeof val === "number" ? val : Number(String(val).replace(/[^0-9.-]/g, ""));
		return Number.isFinite(n) ? n : Number.NEGATIVE_INFINITY;
	};
	const cmpNum = (a: number, b: number) => (!order ? a - b : b - a);
	const cmpStr = (a: string | null | undefined, b: string | null | undefined) =>
		!order ? (a ?? "").localeCompare(b ?? "", undefined, { sensitivity: "base" }) : (b ?? "").localeCompare(a ?? "", undefined, { sensitivity: "base" });
	const cmpDate = (a: Date | null | undefined, b: Date | null | undefined) => {
		const ad = (a ?? old).getTime();
		const bd = (b ?? old).getTime();
		return cmpNum(ad, bd);
	};
	dt.sort((a, b) => {
		let result = 0;
		switch (cols[orderBy]) {
			case "Proposal #":
				result = cmpNum(parseNum(a.InvoiceNumber), parseNum(b.InvoiceNumber));
				break;
			case "Building":
				result = cmpStr(a.BuildingName, b.BuildingName);
				break;
			case "Amount":
				result = cmpNum(Number(a.TotalAmount) || 0, Number(b.TotalAmount) || 0);
				break;
			case "Date":
				result = cmpDate(a.InvoiceDate, b.InvoiceDate);
				break;
			case "Description":
				result = cmpStr(a.Description, b.Description);
				break;
			case "Status":
				result = cmpStr(a.Status, b.Status);
				break;
			case "Mechanic":
				result = cmpStr(a.MechanicName, b.MechanicName);
				break;
			case "Date Signed":
				result = cmpDate(a.DateMechanicSigned, b.DateMechanicSigned);
				break;
			case "Invoice #":
				result = cmpNum(parseNum(a.ActualInvoiceNumber), parseNum(b.ActualInvoiceNumber));
				break;
			case "Date Invoiced":
				result = cmpDate(a.DateInvoiced, b.DateInvoiced);
				break;
			case "Notes":
				result = cmpNum(a.NoteCount ?? 0, b.NoteCount ?? 0);
				break;
		}
		if (result !== 0) return result;
		return (a.InvoiceHeaderId ?? "").localeCompare(b.InvoiceHeaderId ?? "");
	});

	const thisData = dt.slice(pageNumber * pageSize, (pageNumber + 1) * pageSize);
	const rowCounts = [5, 10, 25, 50];

	const handleSort = (idx: number) => {
		setOrderBy(idx);
		setOrder(orderBy === idx ? !order : true);
	};

	return (
		<>
			<ProposalTrackingNotesDialog
				open={notesModal}
				invoiceHeaderId={notesProposal?.InvoiceHeaderId ?? ""}
				proposalNumber={notesProposal?.InvoiceNumber ?? ""}
				buildingName={notesProposal?.BuildingName ?? ""}
				onClose={() => setNotesModal(false)}
				onSaved={getData}
			/>
			<ProposalDetailDialog
				open={modal}
				proposal={selectedProposal}
				close={() => {
					setModal(false);
					setSelectedProposal(null);
				}}
			/>
			<PortalTableLayout
				fillHeight={true}
				toolbar={
					<Box
						sx={{
							display: "flex",
							flexWrap: "wrap",
							alignItems: "center",
							justifyContent: "space-between",
							gap: 1.5,
							backgroundColor: "background.surface",
							border: "1px solid",
							borderColor: "divider",
							p: 1.5,
						}}>
						<Typography level="title-lg" sx={{ m: 0 }}>
							Proposal Tracking
						</Typography>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
							<Select placeholder="All Statuses" value={statusFilter} sx={{ minWidth: 150 }} onChange={(event, value) => setStatusFilter(value as string)}>
								<Option value="">All</Option>
								{statusOptions.map((status) => (
									<Option key={status} value={status}>
										{status}
									</Option>
								))}
							</Select>
							<Select
								placeholder="All Buildings"
								value={isMyBuildings ? "My Buildings" : "All Buildings"}
								sx={{ minWidth: 150 }}
								onChange={(event, value) => setIsMyBuildings(value === "My Buildings")}>
								<Option value="All Buildings">All Buildings</Option>
								<Option value="My Buildings">My Buildings</Option>
							</Select>
							<FormControl>
								<Input
									name="Search"
									placeholder="Search Invoice # or Building"
									onChange={(event) => {
										setGlobalFilter(event.target.value);
									}}
									value={globalFilter}
									sx={{ minWidth: 250 }}
								/>
							</FormControl>
						</Box>
					</Box>
				}
				pagination={{
					count: dt.length,
					page: pageNumber,
					rowsPerPage: pageSize,
					rowsPerPageOptions: rowCounts,
					onPageChange: (_event, newPage) => setPageNumber(newPage),
					onRowsPerPageChange: (event) => {
						setPageSize(parseInt(event.target.value, 10));
						setPageNumber(0);
					},
				}}>
				<Table stickyHeader size="small">
					<TableHead>
						<TableRow>
							{cols.map((x, idx) => (
								<TableCell
									key={idx}
									sortDirection={orderBy === idx ? (order ? "desc" : "asc") : false}
									sx={{ width: idx === 4 ? "25%" : idx === 5 ? "10%" : "auto" }}>
									<TableSortLabel active={orderBy === idx} direction={orderBy === idx ? (order ? "desc" : "asc") : "asc"} onClick={() => handleSort(idx)}>
										{x}
									</TableSortLabel>
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{thisData?.map((x) => (
							<TableRow
								key={x.InvoiceHeaderId}
								hover
								sx={{ cursor: "pointer" }}
								onClick={() => {
									setModal(true);
									setSelectedProposal(x);
								}}>
								<TableCell>{x.InvoiceNumber}</TableCell>
								<TableCell>{x.BuildingName}</TableCell>
								<TableCell>{formatCurrency(x.TotalAmount)}</TableCell>
								<TableCell>{Util.Formatter(x.InvoiceDate, "D")}</TableCell>
								<TableCell sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 200 }}>{x.Description}</TableCell>
								<TableCell>
									<Chip variant="soft" color={getStatusColor(x.Status)} size="sm">
										{x.Status}
									</Chip>
								</TableCell>
								<TableCell>{x.MechanicName}</TableCell>
								<TableCell>{Util.Formatter(x.DateMechanicSigned, "D")}</TableCell>
								<TableCell>{x.ActualInvoiceNumber}</TableCell>
								<TableCell>{Util.Formatter(x.DateInvoiced, "D")}</TableCell>
								<TableCell>
									<Button
										size="sm"
										color="primary"
										startDecorator={x.NoteCount > 0 ? undefined : <AddIcon />}
										onClick={(event) => {
											event.stopPropagation();
											setNotesProposal(x);
											setNotesModal(true);
										}}>
										{x.NoteCount > 0 ? `Notes (${x.NoteCount})` : "Add Note"}
									</Button>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</PortalTableLayout>
		</>
	);
}
